import React, { useState, useEffect, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import camelize from 'camelize';
import queryString from 'query-string';
import { useUserContext } from 'context';
import { useToggle, useClickOutside } from 'hooks';
import { urls } from 'app-constants';
import Icon from 'components/Icon';
import DropdownMenu from 'components/DropdownMenu';

const EditorSearch = ({ className }) => {
  const { isStaff, isZeditor } = useUserContext();

  const containerRef = useRef();
  const inputRef = useRef();
  const [inputVal, setInputVal] = useState('');
  const trimmedVal = inputVal.trim();

  const [menuVisible, toggleMenuVisible] = useToggle(false);
  const handleClickOutside = () => toggleMenuVisible(false);
  useClickOutside(containerRef, handleClickOutside);

  const handleFormSubmit = evt => evt.preventDefault();
  const handleInputChange = evt => setInputVal(evt.target.value);

  const [eventData, setEventData] = useState(null);
  const [poiData, setPoiData] = useState(null);
  const fetchTimeout = useRef();
  const fetchResults = query => {
    clearTimeout(fetchTimeout.current);
    fetchTimeout.current = setTimeout(() => {
      const query = queryString.stringify({ q: trimmedVal });
      const eventUrl = `${urls.eventLivesearch}?${query}`;
      const poiUrl = `${urls.poiLivesearch}?${query}`;

      Promise.all([eventUrl, poiUrl].map(url => (
        fetch(url)
          .then(response => {
            if (!response.ok) throw new Error(response.statusText);
            return response.json();
          })
          .catch(err => {
            console.error(err);
          })
      ))).then(([eventData, poiData]) => {
        setEventData(camelize(eventData) || []);
        setPoiData(camelize(poiData) || []);
      });
    }, 500);
  };

  useEffect(() => {
    if (trimmedVal.length > 2) {
      fetchResults(trimmedVal);
    } else {
      setEventData(null);
      setPoiData(null);
    }
  }, [trimmedVal]);

  const iconStyle = {
    fontSize: 14,
    position: 'absolute',
    left: 20,
    top: '50%',
    transform: 'translateY(-50%)',
  };

  const results = [
    ['Events:', eventData],
    ['POIs:', poiData],
  ].map(([label, data]) => !!data && (
    <Fragment key={label}>
      <div className="link-list-separator px-4 has-label" style={{ borderTop: 0 }}>{label}</div>
      {data.length === 0 ? (
        <div className="link-list-item noclick px-4"><em>No results</em></div>
      ) : data.slice(0, 10).map(item => (
        <a key={item.id} className="link-list-item px-4" href={item.absoluteUrl}>{item.name}</a>
      ))}
    </Fragment>
  ));

  return (isStaff || isZeditor) && (
    <form
      ref={containerRef}
      autoComplete="off"
      className={`keyword-search ${className}`}
      onSubmit={handleFormSubmit}
      data-keyword-value={trimmedVal}
    >
      <input
        ref={inputRef}
        autoComplete="off"
        className="btn-z subtle-focus"
        style={{ width: 220 }}
        name="keyword"
        type="text"
        placeholder="Search Events / POIs"
        value={inputVal}
        onFocus={() => toggleMenuVisible(true)}
        onChange={handleInputChange}
      />
      <Icon i={['far', 'magnifying-glass-plus']} style={iconStyle} />

      <DropdownMenu
        containerRef={containerRef}
        isOpen={menuVisible}
      >
        <div className="link-list" style={{ minWidth: 380 }}>
          {inputVal.length > 2 ? results : (
            <div className="link-list-item noclick px-4"><em>Enter search query</em></div>
          )}
        </div>
      </DropdownMenu>
    </form>
  );
};

EditorSearch.propTypes = {
  className: PropTypes.string,
};

export default EditorSearch;
